import React, { Component } from 'react';
import SimpleTable from 'components/shared/table/SimpleTable';
import PropTypes from 'prop-types';
import './AnticipatedCostReportTable.css';
import Number from 'components/shared/text/Number';
import numeral from 'numeral';
import Button from 'components/shared/button/Button';
import Input from 'components/shared/input/Input';
import TextArea from 'components/shared/input/TextArea';

class AnticipatedCostReportTable extends Component {
  static propTypes = {
    workOrder: PropTypes.object,
    proposals: PropTypes.array,
    quotes: PropTypes.array,
    changeOrderTasks: PropTypes.array,
    changeOrders: PropTypes.array,
    miscCosts: PropTypes.object,
    edit: PropTypes.object,
    saving: PropTypes.object,
    onValueChange: PropTypes.func,
    onEdit: PropTypes.func,
    onSave: PropTypes.func,
    proposal: PropTypes.object,
    additionalCostEdit: PropTypes.bool,
    additionalCostSaving: PropTypes.bool,
    onAdditionalCostValueChange: PropTypes.func,
    onAdditionalCostEdit: PropTypes.func,
    onAdditionalCostSave: PropTypes.func,
  };

  handleClick = (quoteId) => {
    if (this.props.edit[quoteId]) {
      if (this.props.onSave) {
        this.props.onSave(quoteId);
      }
    } else {
      if (this.props.onEdit) {
        this.props.onEdit(quoteId);
      }
    }
  };

  handleChange = (quoteId, value) => {
    if (this.props.onValueChange) {
      this.props.onValueChange(quoteId, value);
    }
  };

  handleAdditionalCostChange = (value, attr) => {
    if (this.props.onAdditionalCostValueChange) {
      this.props.onAdditionalCostValueChange(value, attr);
    }
  };

  handleAdditionalCostClick = () => {
    if (this.props.additionalCostEdit) {
      if (this.props.onAdditionalCostSave) {
        this.props.onAdditionalCostSave();
      }
    } else {
      if (this.props.onAdditionalCostEdit) {
        this.props.onAdditionalCostEdit();
      }
    }
  };

  getCoOriginalCosts = (taskId) => {
    let coOriginalCosts = 0;
    const { changeOrders } = this.props;
    this.props.changeOrderTasks
      .filter((cot) => cot.status === 'approved' && cot.task_id === taskId)
      .forEach(function (cot) {
        coOriginalCosts +=
          numeral(cot.change_order_quote.estimate).value() +
          numeral(cot.change_order_quote.value).value();

        changeOrders
          .filter((co) => co.status === 'approved')
          .forEach(function (co) {
            co.change_order_proposal_tasks
              .filter((copt) => copt.change_order_task_id === cot.id)
              .forEach(function (copt) {
                coOriginalCosts +=
                  numeral(copt.markup).value() +
                  numeral(copt.fee).value() +
                  numeral(copt.insurance).value();
              });
          });
      });

    return coOriginalCosts;
  };

  getCoCommittedCosts = (taskId) => {
    return this.props.changeOrderTasks
      .filter(
        (cot) =>
          cot.task_id === taskId &&
          (cot.status === 'approved' || cot.status === 'acr')
      )
      .reduce(function (total, cot) {
        total += numeral(cot.change_order_quote.estimate).value();
        return total;
      }, 0);
  };

  render() {
    this.rows = this.createRows();
    const headers = [
      'TASK #',
      'TRADE',
      'ORIGINAL',
      'CHANGE ORDER',
      'COMMITTED',
      'PENDING',
      'VARIANCE ($)',
      'VARIANCE (%)',
      '',
    ];

    return (
      <div className="anticipated-cost-report-table-container">
        <SimpleTable
          className="anticipated-cost-report-table"
          headers={headers}
          rows={this.rows}
          emptyMessage="No Quotes"
        />
      </div>
    );
  }

  createRows() {
    const rows = [];
    let totalOriginal = 0;
    let totalChangeOrder = 0;
    let totalClientCost = 0;
    let totalCommitted = 0;
    let totalPending = 0;
    let totalVariance = 0;

    this.props.quotes
      .filter((q) => q.status === 'approved')
      .sort((quote1, quote2) => {
        return quote1.task.number - quote2.task.number;
      })
      .forEach((quote) => {
        let task = quote.task;

        rows.push({
          id: `t${task.id}`, // prefix with t to avoid same id with quote
          data: [
            <span className="h2">Task # {task.number}</span>,
            '',
            '',
            '',
            '',
            '',
            '',
          ],
        });

        let proposal = this.props.proposals.find(
          (p) => p.id === this.props.workOrder.approved_proposal.id
        );
        let proposalQuote = proposal.proposal_quotes.find(
          (pq) => pq.quote_id === quote.id
        );
        let clientCost =
          numeral(quote.estimate).value() +
          numeral(quote.value).value() +
          numeral(proposalQuote.fee).value() +
          numeral(proposalQuote.insurance).value() +
          numeral(proposalQuote.markup).value();
        let changeOrderCost = this.getCoOriginalCosts(task.id);
        let clientTotalCost = clientCost + changeOrderCost;
        let committedCost =
          numeral(quote.estimate).value() + this.getCoCommittedCosts(task.id);
        let miscCost = this.props.miscCosts[quote.id] || 0;
        let varianceCost = clientTotalCost - committedCost - miscCost;
        let variance = (varianceCost * 100) / clientTotalCost;

        let buttonLabel;
        if (this.props.edit[quote.id]) {
          if (this.props.saving[quote.id]) {
            buttonLabel = 'Saving...';
          } else {
            buttonLabel = 'Save';
          }
        } else {
          buttonLabel = 'Edit';
        }

        totalOriginal += numeral(clientCost).value();
        totalChangeOrder += changeOrderCost;
        totalClientCost = totalOriginal + totalChangeOrder;
        totalCommitted += numeral(committedCost).value();
        totalPending += numeral(miscCost).value();
        totalVariance = totalClientCost - totalCommitted - totalPending;

        let data = [
          quote.contractor_name,
          task.trade.label,
          <Number value={clientCost} format="$0,0" />,
          <Number value={changeOrderCost} format="$0,0" />,
          <Number value={committedCost} format="$0,0" />,
          this.props.edit[quote.id] ? (
            <Input
              type="number"
              readOnly={this.props.saving[quote.id]}
              value={this.props.miscCosts[quote.id]}
              onChange={(value) => this.handleChange(quote.id, value)}
            />
          ) : (
            <Number value={miscCost} format="$0,0.00" />
          ),
          <Number value={varianceCost} format="$0,0" />,
          <div>
            <Number value={variance} format="0,0" />%
          </div>,
          <Button
            className="button-orange"
            disabled={this.props.saving[quote.id]}
            label={buttonLabel}
            onClick={() => this.handleClick(quote.id)}
          />,
        ];
        rows.push({ id: quote.id, data });
      });

    let buttonLabel;
    if (this.props.additionalCostEdit) {
      if (this.props.additionalCostSaving) {
        buttonLabel = 'Saving...';
      } else {
        buttonLabel = 'Save';
      }
    } else {
      buttonLabel = 'Edit';
    }

    rows.push({
      id: 'Totals',
      className: 'totals',
      data: [
        '',
        '',
        <span className="h2">Totals</span>,
        '',
        '',
        '',
        '',
        '',
        '',
      ],
    });

    rows.push({
      id: 'TotalsValues',
      className: 'totals',
      data: [
        '',
        '',
        <Number value={totalOriginal} format="$0,0" />,
        <Number value={totalChangeOrder} format="$0,0" />,
        <Number value={totalCommitted} format="$0,0" />,
        <Number value={totalPending} format="$0,0.00" />,
        <Number value={totalVariance} format="$0,0" />,
        <div>
          <Number
            value={(totalVariance * 100) / totalClientCost}
            format="0,0"
          />
          %
        </div>,
        '',
      ],
    });

    rows.push({
      id: 'AdditionalCost',
      data: [
        <span className="h2">Additional Costs</span>,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
    });

    rows.push({
      id: 'AdditionalCostValues',
      data: [
        this.props.additionalCostEdit ? (
          <TextArea
            readOnly={this.props.additionalCostSaving}
            placeholder="Description"
            value={this.props.proposal.additionalCostDesc}
            onChange={(value) =>
              this.handleAdditionalCostChange(value, 'additionalCostDesc')
            }
          />
        ) : (
          <span>{this.props.proposal.additionalCostDesc || 'TBD'}</span>
        ),
        this.props.additionalCostEdit ? (
          <Input
            type="number"
            readOnly={this.props.additionalCostSaving}
            value={this.props.proposal.additionalCost}
            onChange={(value) =>
              this.handleAdditionalCostChange(value, 'additionalCost')
            }
          />
        ) : (
          <Number value={this.props.proposal.additionalCost} format="$0,0.00" />
        ),
        <Button
          className="button-orange"
          disabled={this.props.additionalCostSaving}
          label={buttonLabel}
          onClick={this.handleAdditionalCostClick}
        />,
        '',
        '',
        '',
        '',
        '',
        '',
        '',
      ],
    });

    return rows;
  }
}

export default AnticipatedCostReportTable;



// WEBPACK FOOTER //
// ./src/components/admin/work-orders/details/anticipated-cost-report/AnticipatedCostReportTable.js